import { useEffect, useState } from "react";
import useGlobalContext from "../../hooks/useGlobalContext";
import {
  getClearZoneModel,
  getZonesFilteringOptions,
} from "../../common/EntitiesManagement";
import localize from "../../common/localize";
import EntityPrinter from "../../components/common/EntityPrinter";
import { generateNoDataValues, getFiltersInfo } from "../../common/utils";
import ErrorMessage from "../../components/common/ErrorMessage";
import { Card, CardContent } from "@mui/material";
import PageTitleSubtitle from "../../components/PageTitleSubTitle";
import SearcherAndAdd from "../../components/SearcherAndAdd";
import DeleteConfirmationMessage from "../../components/common/DeleteConfirmationMessage";
import TableComponent from "../../components/common/TableComponent";
import PagePagination from "../../components/PagePagination";
import ActionButtons from "../../components/common/ActionButtons";
import AddOrEditZone from "./components/AddOrEditZone";
import ValueOrNoDataLabel from "../../components/common/ValueOrNoDataLabel";
import { mutate } from "swr";

export default function Zone() {
  const {
    zoneModel,
    setZoneModel,
    config,
    setBasicFilterOptions,
    basicFilterData,
    toggleVisibleModalConfirm,
    LOCATION_API,
    pageIndexZones,
    setPageIndexZones,
    zonesData,
    deleteEntity,
    locations,
    cities,
  } = useGlobalContext();

  const [zonesFiltered, setZonesFiltered] = useState(zonesData.Data);
  const [showAddModal, setshowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);

  useEffect(() => {
    setBasicFilterOptions(getZonesFilteringOptions());
  }, []);

  useEffect(() => {
    setZonesFiltered(zonesData.Data);
  }, [zonesData]);

  const validateOperationResult = (result, isDelete) => {
    setZoneModel(getClearZoneModel());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.Data ?? result.data));
      setShowErrorModal(true);
      return;
    }

    setshowAddModal(false);
    setShowEditModal(false);

    mutate([LOCATION_API, config]);

    if (isDelete) {
      if (pageIndexZones !== 0 && zonesData.Data.length === 1) {
        setPageIndexZones(pageIndexZones - 1);
      }
    }

    window.location.reload(false);
  };

  const handleDeleteRequest = async (itemId) => {
    const deleteResult = await deleteEntity(
      `${`${LOCATION_API}/${itemId}`}`,
      config,
    );
    toggleVisibleModalConfirm();
    validateOperationResult(deleteResult, true);
  };

  const showPrinter = () => {
    setShowPrinterModal(true);
  };

  const getColumnTitles = () => {
    return [
      localize.code,
      localize.name,
      localize.department,
      localize.city,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  const getGrandParentName = (code) => {
    if (locations?.Data?.length > 0) {
      const location = locations.Data.find((item) => item.Code === code);
      return location ? location.Name : undefined;
    }

    return undefined;
  };

  const getParentName = (code) => {
    if (cities?.Data?.length > 0) {
      const location = cities?.Data.find((item) => item.Code === code);
      return location ? location.Name : undefined;
    }

    return undefined;
  };

  const getStateFromCode = (code) => {
    if (locations?.Data?.length > 0) {
      const location = locations.Data.find((item) => item.Code === code);
      return location;
    }

    return undefined;
  };

  const getCityFromCode = (code) => {
    if (cities?.Data?.length > 0) {
      const location = cities?.Data.find((item) => item.Code === code);
      return location;
    }

    return undefined;
  };

  return (
    <div className="lateral_padding">
      {showEditModal && (
        <AddOrEditZone
          zoneModel={{
            ...zoneModel,
            State: getStateFromCode(zoneModel.GrandParentCode),
            City: getCityFromCode(zoneModel.ParentCode),
          }}
          showModalOnScreen={showEditModal}
          setShowModalOnScreen={setShowEditModal}
          callBack={validateOperationResult}
        />
      )}

      {showAddModal && (
        <AddOrEditZone
          zoneModel={{
            ...zoneModel,
          }}
          showModalOnScreen={showAddModal}
          setShowModalOnScreen={setshowAddModal}
          callBack={validateOperationResult}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          title={localize.print}
          report_title={localize.zones}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={LOCATION_API}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="zones"
        />
      )}

      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showDeleteModal && (
        <DeleteConfirmationMessage
          entityName={`${zoneModel.Name} (${zoneModel.Code})`}
          entityType={localize.zone}
          onClickForCancel={() => {
            toggleVisibleModalConfirm();
            setShowDeleteModal(false);
            setZoneModel(getClearZoneModel());
          }}
          onClickForConfirm={() => {
            handleDeleteRequest(zoneModel.Id);
            setShowDeleteModal(false);
            setZoneModel(getClearZoneModel());
          }}
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.zones}
            subtitle={localize.zones_description}
          />

          <SearcherAndAdd
            printButtonMethod={showPrinter}
            entityName="zones"
            handleAddBtn={() => {
              setZoneModel(getClearZoneModel());
              setshowAddModal(true);
            }}
          />
        </CardContent>
      </Card>

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexZones}
            setPageIndex={setPageIndexZones}
            dataArray={zonesData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          zonesFiltered?.length > 0
            ? zonesFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      onClickForEditButton={() => {
                        setZoneModel(item);
                        setShowEditModal(true);
                      }}
                      onclickForDeleteButton={() => {
                        setZoneModel(item);
                        setShowDeleteModal(true);
                        toggleVisibleModalConfirm();
                      }}
                    />
                  </div>,
                  item.Code,
                  item.Name,
                  <ValueOrNoDataLabel
                    data={getGrandParentName(item.GrandParentCode)}
                  />,
                  <ValueOrNoDataLabel data={getParentName(item.ParentCode)} />,
                  new Date(item.CreationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(7)]
        }
      />
    </div>
  );
}
